/**
 * The kits and sprites that ship inside the page, registered once.
 *
 * Both pages need the same thing: the built-in kits present before anything
 * renders, and the example specs typed as specs. Doing that in each of them
 * meant two copies of the same import list and two chances for the welcome page
 * to demonstrate a sprite the workbench cannot open.
 *
 * Importing this module registers the kits as a side effect. That is the point
 * of it, and it is why it is imported for effect rather than only for values.
 */

import { addGlyphs, parseKit, registerKit } from "../src/kit.js";
import type { View } from "../src/ask.js";
import type { SpriteSpec } from "../src/spec.js";

import colonyKit from "../kits/colony.json";
import oldWoodKit from "../kits/old_wood.json";
import arcadeKit from "../kits/arcade.json";
import sharedGlyphs from "../kits/glyphs.json";

import batterySpec from "../specs/battery.json";
import doorSpec from "../specs/wooden_door.json";
import coopSpec from "../specs/chicken_coop.json";
import missileSpec from "../specs/examples/missile.json";
import mugSpec from "../specs/examples/mug.json";
import plantSpec from "../specs/examples/plant.json";
import gallery from "../specs/gallery/gallery.json";
import fromPhoto from "../specs/gallery/from-photo.json";

addGlyphs((sharedGlyphs as { glyphs: Record<string, string[]> }).glyphs, "glyphs.json");
for (const raw of [arcadeKit, colonyKit, oldWoodKit]) registerKit(parseKit(raw, "built in"));

/**
 * A spec that arrived as data rather than as code.
 *
 * TypeScript infers `size` from a JSON file as `number[]`, which is not the
 * `[number, number]` the format promises, so every bundled sprite needs the
 * cast. The validator is what actually decides whether one of these is a spec,
 * and it runs on every render.
 */
export const asSpec = (raw: unknown) => raw as SpriteSpec;

/**
 * Sprites the tool actually drew, kept with the sentence that produced them.
 *
 * These are the welcome page's gallery, and they are model output rather than
 * hand-authored specs, which is the entire reason they are worth showing.
 * src/gallery.test.ts renders every one on each test run, because generated
 * data is the first thing a change to a shape or a kit breaks and the last
 * thing anybody thinks to check.
 */
export interface Drawn {
	want: string;
	/** Which angle was asked for, so the gallery can show that the control works. */
	view: View;
	spec: SpriteSpec;
}

export const GALLERY: Drawn[] = (gallery as unknown[]).map((e) => e as Drawn);

/**
 * The one drawn from a photograph rather than from a sentence alone.
 *
 * Kept apart from the gallery because the gallery is a deliberate three-by-three
 * of viewing angles and a tenth entry would break both the grid and the test
 * that guards it. This one is making a different point and gets its own place
 * on the page to make it.
 */
export const FROM_PHOTO = fromPhoto as unknown as Drawn & { from: string };

/** Bundled sprites, so the format can be poked at without asking for a drawing. */
export const EXAMPLES: Record<string, SpriteSpec> = {
	mug: asSpec(mugSpec),
	plant: asSpec(plantSpec),
	missile: asSpec(missileSpec),
	battery: asSpec(batterySpec),
	"chicken coop": asSpec(coopSpec),
	"wooden door": asSpec(doorSpec),
};
